import { IconStar, IconUser, IconExternalLink } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import Loader from "../../components/Loader";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { Avatar } from "../../components/ui/Avatar";
import type { SupportedLanguage } from "../../interfaces";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { useNow } from "../../common/hooks/useNow";
import { resolveCongressNames } from "../../domains/congress/congressName";
import { deriveCongressPhase } from "../../../../api/src/shared/domain/congress-lifecycle";
import { resolveTopSessionsHeading } from "../../domains/congress/topSessionsHeading";
import { gaEventCongressSessionLinkClicked } from "../../tools/analytics/domains/congress.analytics";
import { useApiQuery, type Api } from "../../services/api";

type TopSessionsBody =
  Api["get"]["/webapp/private/congress-spaces/:id/top-sessions"]["output"]["body"];
type SessionRow = TopSessionsBody["sessions"][number];

const MINUTE_MS = 60_000;

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  const letters = parts
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
  return letters === "" ? "?" : letters.slice(0, 2);
}

// The weekday and clock time of a session, in the CONTENT language: the session
// title and takeaway beside it are content, so a French weekday next to an
// English title would read as two languages in one line. UTC because a congress
// programme is published in the congress's own calendar, not the viewer's zone.
function formatSessionTime(
  startsAt: string,
  contentLang: SupportedLanguage
): string {
  const date = new Date(startsAt);
  const day = date.toLocaleDateString(contentLang, {
    weekday: "short",
    timeZone: "UTC",
  });
  const time = date.toLocaleTimeString(contentLang, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  });
  return `${day} ${time}`;
}

function SessionItem({
  session,
  index,
  contentLang,
  onLinkClick,
  t,
}: {
  session: SessionRow;
  index: number;
  /** Content language of this session — its time line follows it */
  contentLang: SupportedLanguage;
  onLinkClick: () => void;
  /** Interface-locale chrome ("See session details") */
  t: (key: string) => string;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-ds-slate-100 p-4">
      <div className="flex items-center gap-3">
        <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-ds-blackberry-600 text-xs font-semibold text-white">
          {index + 1}
        </span>
        <p className="m-0 text-base font-semibold text-ds-black">
          {session.title}
        </p>
      </div>
      <p className="m-0 text-xs text-ds-slate-500">
        {session.roomLocation} ·{" "}
        {formatSessionTime(session.startsAt, contentLang)}
      </p>
      <p className="m-0 text-sm/5 text-ds-slate-700">“{session.takeaway}”</p>
      {session.sessionUrl !== "" && (
        <a
          href={session.sessionUrl}
          target="_blank"
          rel="noreferrer"
          onClick={onLinkClick}
          className="inline-flex items-center gap-1 text-sm font-medium text-ds-mint-700"
        >
          {t("See session details")}
          <IconExternalLink className="size-4 stroke-[1.5px]" />
        </a>
      )}
    </div>
  );
}

export default function CongressTopSessionsPage({
  roomId,
  spaceId,
}: {
  roomId: string;
  spaceId: string;
}) {
  const { t } = useTranslation();
  // ONE binding on purpose. Everything this page draws from the payload — the
  // congress name, the heading, the curator copy, each session's time — follows
  // `contentLang`, the axis the payload itself was fetched on. The page chrome
  // ("Curator's note", "See session details") stays on the interface locale via
  // bare `t()`. See the axis rule in `useActiveLang`.
  const { contentLang } = useActiveLang();
  const now = useNow(MINUTE_MS);
  const result = useApiQuery(
    "/webapp/private/congress-spaces/:id/top-sessions",
    { param: { id: spaceId }, query: { language: contentLang } }
  );

  if (result.state === "pending") return <Loader expanded />;
  // Same exit as the congress PDF page: back to the room's Resources tab.
  if (result.state === "error") {
    return <Redirect to={`/room/resources/${roomId}`} />;
  }

  const space = result.body;
  const { name, shortName } = resolveCongressNames(
    space.translations,
    space.fallbackName,
    contentLang,
    space.city,
    space.country
  );
  const curator = space.curator;
  const analytics = {
    spaceId,
    congressName: name,
    phase: deriveCongressPhase(space, now),
    roomId,
  };

  return (
    <ScrollableLayout header={<ContentHeader transparent />} footer={null}>
      <div className="flex flex-col gap-6 bg-white px-5 pb-12 pt-4 font-dm-sans">
        <div className="flex flex-col gap-3 rounded-2xl bg-secondary-green-2 p-4">
          {/* Icon aligns to the heading's FIRST LINE, with the wrapper's height
              derived from `--tsh-leading` — see the note in
              CongressTopSessionsCard for why, and why the height must not be a
              px constant. Kept identical to that card on purpose: it is the same
              heading, and the card is what opens this page. */}
          <div className="flex items-start gap-2 text-ds-mint-800 [--tsh-leading:1rem]">
            <span className="flex h-(--tsh-leading) shrink-0 items-center">
              <IconStar className="size-5 stroke-[1.5px]" />
            </span>
            <p className="m-0 text-sm font-semibold uppercase leading-(--tsh-leading) tracking-wide">
              {resolveTopSessionsHeading({
                cardTitle: space.topSessionsCardTitle,
                shortName,
                sessionCount: space.sessions.length,
                contentLang,
                t,
              })}
            </p>
          </div>
          {curator.name !== null && (
            <div className="flex items-center gap-3">
              {/* 52px (`lg`), intentionally the SAME as the Top Sessions card
                  that opens this page — see the note in CongressTopSessionsCard
                  for why the two were flattened to one size and must stay in
                  sync. */}
              {curator.avatarUrl !== null ? (
                <Avatar
                  variant="image"
                  src={curator.avatarUrl}
                  alt={curator.name}
                  imageFit="cover"
                  size="lg"
                  badge={null}
                />
              ) : (
                <Avatar
                  variant="initials"
                  initials={initialsOf(curator.name)}
                  size="lg"
                  badge={null}
                />
              )}
              <div className="flex flex-col">
                <span className="text-xs uppercase tracking-wide text-ds-slate-500">
                  {curator.role}
                </span>
                <span className="text-sm font-semibold text-ds-black">
                  {curator.name}
                </span>
              </div>
            </div>
          )}
          {curator.bio !== null && curator.bio !== "" && (
            <p className="m-0 text-xs text-ds-slate-600">{curator.bio}</p>
          )}
          {curator.name === null && (
            <div className="flex items-center gap-2 text-ds-slate-400">
              <IconUser className="size-5 stroke-[1.5px]" />
            </div>
          )}
        </div>

        {curator.selectionNote !== null && curator.selectionNote !== "" && (
          <div className="flex flex-col gap-2">
            <p className="m-0 text-base font-semibold text-ds-mint-800">
              {t("Curator's note")}
            </p>
            <p className="m-0 text-sm/6 text-ds-slate-700">
              {curator.selectionNote}
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {space.sessions.map((session, index) => (
            <SessionItem
              key={session.id}
              session={session}
              index={index}
              contentLang={contentLang}
              onLinkClick={() => {
                gaEventCongressSessionLinkClicked({
                  context: analytics,
                  sessionId: session.id,
                  position: index,
                });
              }}
              t={t}
            />
          ))}
        </div>
      </div>
    </ScrollableLayout>
  );
}
